// One day's performance dataset, built from what the app already records: clock sessions,
// task time entries, completions, skips, breaks, notes, travel and the card-flow telemetry.
// Pure — no I/O, no dates from the wall clock unless `now` is omitted. The Daily Log renders
// this; exports/analytics read the same object.
//
// Every duration is clipped to the day. A session that ran past midnight (forgot to clock out)
// counts up to 23:59:59 and is flagged, never as 14 hours of work.

import { planItems, actualItems, planVsActual } from './calendar.js';
import { photosOfTask, mediaForDay } from './photoindex.js';
import { departmentIndex, GENERAL_DEPARTMENT_ID } from './hierarchy.js';
import { workDepartments } from './workdept.js';
import { distractingApps, phoneDayMetrics, phoneScore, phoneGoals, phoneMsInWindows } from './phonestats.js';
import { routineDay } from './routines.js';
import { dayMoney, agentUids } from './profit.js';
import { tagIndex as tagIndexOf } from './tags.js';
import { earnedByJobType } from './jobpay.js';
import { isVoidTask, voidedOn } from './projectclose.js';

const DAY_MS = 86400000;
const MIN_SESSION_MS = 60000;

export function dayBounds(date) {
  const startMs = new Date(date + 'T00:00:00').getTime();
  return { startMs, endMs: startMs + DAY_MS };
}

const inDay = (ms, b) => typeof ms === 'number' && ms >= b.startMs && ms < b.endMs;
const isImage = (u) => typeof u === 'string' && /^(https?:|file:|content:|data:image)/.test(u);

// Clip a session to [day start, min(day end, now)]. Returns null if nothing of it falls in the day.
function clipSession(s, b, now) {
  const start = Math.max(s.startedAt || 0, b.startMs);
  const open = !s.endedAt;
  const rawEnd = open ? now : s.endedAt;
  const end = Math.min(rawEnd, b.endMs, now);
  if (!(end > start)) return null;
  return {
    ...s,
    start, end,
    ms: end - start,
    open: open && now < b.endMs,             // still running today
    ranPastMidnight: rawEnd > b.endMs,       // ended (or is still open) on a later day
  };
}

function photosOf(task, media = []) {
  return photosOfTask(task, media).filter((p) => isImage(p.uri)).map((p) => ({
    uri: p.uri, tag: p.tag, taskId: task.id, title: task.title, at: p.at || null, caption: p.caption || null, ...(p.cover ? { coords: task.photoCoords || null } : {}),
  }));
}

// Share of a travel session's (clipped) time spent on segments that pass `keep(projectId)`.
// taskSegments say which project's task was live while driving; no segments = unattributed.
function segmentShare(s, keep) {
  const segs = Array.isArray(s.taskSegments) ? s.taskSegments : [];
  if (!segs.length) return 0;
  let total = 0, kept = 0;
  for (const g of segs) {
    const a = Math.max(g.startedAt || 0, s.start);
    const z = Math.min(g.endedAt || s.end, s.end);
    if (!(z > a)) continue;
    total += z - a;
    if (keep(g.projectId || null)) kept += z - a;
  }
  return total > 0 ? kept / total : 0;
}

/**
 * @param {object} p
 * @param {string} p.date      YYYY-MM-DD (local)
 * @param {Array}  p.tasks     all task docs
 * @param {Array}  p.sessions  all clockSessions docs
 * @param {Array}  [p.notes]   notes collection docs ({ date?, createdAt, taskId?, projectId?, title, content })
 * @param {Array}  [p.flow]    taskFlow docs ({ kind: 'skip'|'switch', date, dwellMs, ... })
 * @param {Array}  [p.projects], [p.departments]  for the by-department breakdown
 * @param {Set}    [p.projectIds]  scope: only these projects (a department's, or one). Travel keeps
 *                 the share of each drive attributed to them through taskSegments.
 * @param {number} [p.now]
 */
export function buildDayReport({ date, tasks: allTasks = [], sessions = [], notes: allNotes = [], flow: allFlow = [], now = Date.now(), projects = [], departments = [], projectIds = null, phone = null, focus = null, events = [], blocks = [], routines = [], routineRuns = [], uid = null, departmentMode = false, pay = null, jobs = [], overhead = null, members = [], tags = null, media = [] }) {
  const b = dayBounds(date);
  const inScope = (pid) => !projectIds || (pid != null && projectIds.has(pid));
  const tasks = projectIds ? allTasks.filter((t) => inScope(t.projectId)) : allTasks;
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const notes = projectIds ? allNotes.filter((n) => inScope(n.projectId) || (n.taskId && byId.has(n.taskId))) : allNotes;
  const flow = projectIds ? allFlow.filter((f) => inScope(f.projectId)) : allFlow;
  const deptOf = departmentIndex(projects, departments);
  const deptId = (pid) => (pid && deptOf.get(pid)) || GENERAL_DEPARTMENT_ID;
  // D5: in department mode worked time and done tasks land under their task's department (workdept.js).
  const wd = workDepartments({ tasks: allTasks, projects, departments, departmentMode });
  const taskDeptId = (t) => (wd.on && t ? wd.forTask(t)?.id : null) || deptId(t?.projectId);

  // ── Clock ──────────────────────────────────────────────────────────────────────────────────
  const daySessions = sessions
    .filter((s) => s.date === date && !s.migrated)
    .map((s) => clipSession(s, b, now))
    .filter(Boolean)
    .map((s) => {
      if (!projectIds) return s;
      if (s.type !== 'travel') return inScope(s.projectId) ? s : null;
      const f = segmentShare(s, inScope);
      return f > 0 ? { ...s, ms: Math.round(s.ms * f), distanceM: Math.round((s.distanceM || 0) * f), share: f } : null;
    })
    .filter(Boolean)
    .sort((a, c) => a.start - c.start);
  const work = daySessions.filter((s) => s.type !== 'travel' && (s.open || s.ms >= MIN_SESSION_MS || (s.focusedTime || 0) > 0));
  const travel = daySessions.filter((s) => s.type === 'travel' && (s.ms >= MIN_SESSION_MS || (s.path && s.path.length > 1)));
  const clockedMs = work.reduce((sum, s) => sum + s.ms, 0);
  const clockByProject = {};
  work.forEach((s) => { const k = s.projectId || 'none'; clockByProject[k] = (clockByProject[k] || 0) + s.ms; });
  // A work session with no project, no task time and no route is clock churn (e.g. an auto-travel
  // that clocked you in by itself). Reported so it can be cleaned up, excluded from "clocked".
  const phantom = work.filter((s) => !s.projectId && !(s.focusedTime > 0));
  const phantomMs = phantom.reduce((sum, s) => sum + s.ms, 0);
  const real = work.filter((s) => !phantom.includes(s));
  const firstIn = real.length ? real[0].start : null;
  const lastOut = real.length ? Math.max(...real.map((s) => s.end)) : null;
  const clockOpen = real.some((s) => s.open);
  const clockRanPastMidnight = real.some((s) => s.ranPastMidnight);

  // ── Travel ─────────────────────────────────────────────────────────────────────────────────
  // A "drive" of hours that covered no ground never ended (arrival never fired) — flagged, not counted.
  const neverEnded = (s) => s.ms >= 6 * 3600000 && (s.distanceM || 0) < 500;
  const drives = travel.filter((s) => !neverEnded(s));
  const travelMs = drives.reduce((sum, s) => sum + s.ms, 0);
  const travelKm = drives.reduce((sum, s) => sum + (s.distanceM || 0), 0) / 1000;
  const travelOpenAllDay = travel.some(neverEnded);

  // ── Task time (by time entry date, not by the task's scheduled date) ───────────────────────
  const taskTime = [];
  const bucketTime = {};
  tasks.forEach((t) => {
    const entries = (t.timeEntries || []).filter((e) => e && e.date === date && (e.duration || 0) > 0);
    let ms = entries.reduce((sum, e) => sum + (e.duration || 0), 0);
    // Legacy tasks (before time entries existed) only carry timeSpent — count it on their day.
    if (!ms && !(t.timeEntries || []).length && t.scheduledDate === date && (t.timeSpent || 0) > 0) ms = t.timeSpent;
    if (ms < MIN_SESSION_MS) return;   // sub-minute stints are card flicks, not work
    if (t.bucket) { (bucketTime[t.bucket] = bucketTime[t.bucket] || []).push({ taskId: t.id, task: t, ms, projectId: t.projectId || null }); return; }
    taskTime.push({ taskId: t.id, task: t, ms, entries, projectId: t.projectId || null });
  });
  taskTime.sort((a, c) => c.ms - a.ms);
  const workedMs = taskTime.reduce((sum, x) => sum + x.ms, 0);
  // Setup / cleanup (project-type checklists): recorded like task time, reported as its own bucket.
  const bucketMs = (k) => (bucketTime[k] || []).reduce((sum, x) => sum + x.ms, 0);
  const setupMs = bucketMs('setup');
  const cleanupMs = bucketMs('cleanup');
  const workByProject = {};
  taskTime.forEach((x) => { const k = x.projectId || 'none'; workByProject[k] = (workByProject[k] || 0) + x.ms; });
  const longestStretchMs = taskTime.reduce((m, x) => Math.max(m, ...x.entries.map((e) => e.duration || 0), x.entries.length ? 0 : x.ms), 0);

  // ── Breaks ─────────────────────────────────────────────────────────────────────────────────
  const breaks = [];
  tasks.forEach((t) => (t.statusLog || []).forEach((l) => {
    if (!(l.duration > 0)) return;
    const at = l.startedAt || l.at || null;
    const belongs = at ? inDay(at, b) : t.scheduledDate === date;
    if (belongs) breaks.push({ taskId: t.id, context: l.context || l.type || 'break', ms: l.duration, at });
  }));
  breaks.sort((a, c) => (a.at || 0) - (c.at || 0));
  const breakMs = breaks.reduce((sum, x) => sum + x.ms, 0);
  const breakBreakdown = {};
  breaks.forEach((x) => { breakBreakdown[x.context] = (breakBreakdown[x.context] || 0) + x.ms; });

  // ── Tasks ──────────────────────────────────────────────────────────────────────────────────
  const roots = tasks.filter((t) => !t.parentId && !t.bucket);
  const bucketDone = tasks.filter((t) => t.bucket && t.status === 'done' && inDay(t.completedAt, b));
  const completedAll = tasks.filter((t) => !t.bucket && t.status === 'done' && inDay(t.completedAt, b)).sort((a, c) => a.completedAt - c.completedAt);
  const completed = completedAll.map((t, i) => {
    const actualMs = t.timeSpent || 0;
    const estMs = t.estimatedMinutes ? t.estimatedMinutes * 60000 : null;
    const c = t.completion || {};
    return {
      task: t,
      order: i + 1,                                  // nth completion today (derived)
      dayIndex: c.dayIndex || i + 1,                 // stored at completion when available
      projectIndex: c.index || null,                 // nth completion in its project
      plannedIndex: c.plannedIndex || null,          // outline label at the time ("2.3")
      actualMs, estMs,
      varianceMs: estMs != null ? actualMs - estMs : null,
      photos: photosOf(t, media),
      notes: Array.isArray(t.notes) ? t.notes.filter(Boolean) : [],
    };
  });
  const planned = roots.filter((t) => t.scheduledDate === date && !isVoidTask(t));   // a task voided with its project was never finished — and isn't owed either
  const voided = voidedOn(tasks, projects, date);   // "12 tasks voided with The Poly Palace" (projectclose.js)
  const carriedOver = planned.filter((t) => t.status === 'todo');
  const created = tasks.filter((t) => !t.bucket && inDay(t.createdAt, b));
  const skipped = tasks.filter((t) => !t.bucket && inDay(t.lastSkippedAt, b));
  const plannedOrDone = new Set([...planned.map((t) => t.id), ...completedAll.filter((t) => !t.parentId).map((t) => t.id)]);
  const completionRate = plannedOrDone.size ? Math.round((completedAll.filter((t) => !t.parentId).length / plannedOrDone.size) * 100) : 0;
  const estimated = completed.filter((x) => x.estMs != null && x.actualMs > 0);
  const estTotal = estimated.reduce((s, x) => s + x.estMs, 0);
  const actTotal = estimated.reduce((s, x) => s + x.actualMs, 0);
  const estimateAccuracy = estTotal > 0 ? Math.round((actTotal / estTotal) * 100) : null;   // 100 = on estimate, >100 = over
  const earned = completedAll.reduce((s, t) => s + (t.actualValue || t.moneyEarned || 0), 0);

  // ── Notes ──────────────────────────────────────────────────────────────────────────────────
  const dayNotes = notes
    .filter((n) => (n.date ? n.date === date : inDay(n.createdAt, b)))
    .sort((a, c) => (a.createdAt || 0) - (c.createdAt || 0));
  const taskNotes = [];
  tasks.forEach((t) => {
    const evs = Array.isArray(t.noteEvents) ? t.noteEvents : [];
    evs.forEach((e) => { if (e && e.text && inDay(e.at, b)) taskNotes.push({ taskId: t.id, task: t, text: e.text, at: e.at, kind: e.kind || 'note' }); });
    // Untimed legacy notes: show them with the task on the day it was completed.
    if (!evs.length && inDay(t.completedAt, b)) (Array.isArray(t.notes) ? t.notes : []).forEach((text) => { if (text) taskNotes.push({ taskId: t.id, task: t, text, at: null }); });
  });
  taskNotes.sort((a, c) => (a.at || 0) - (c.at || 0));

  // ── Flow (card telemetry) ──────────────────────────────────────────────────────────────────
  const dayFlow = flow.filter((f) => f.date === date);
  const switches = dayFlow.filter((f) => f.kind === 'switch').length;
  const flowSkips = dayFlow.filter((f) => f.kind === 'skip');
  const avgSkipDwellMs = flowSkips.length ? Math.round(flowSkips.reduce((s, f) => s + (f.dwellMs || 0), 0) / flowSkips.length) : 0;

  // ── Photos taken today, on any task, in the order they were taken (Lj, Oct 10: the log tracks when photos were
  // taken, with the note on each). Old photos with no time still show for the tasks touched today.
  const touchedIds = new Set([...planned.map((t) => t.id), ...completedAll.map((t) => t.id), ...taskTime.map((x) => x.taskId)]);
  const photos = [];
  byId.forEach((t) => { for (const p of photosOf(t, media)) if (p.at ? inDay(p.at, b) : touchedIds.has(t.id)) photos.push(p); });
  photos.sort((x, y) => (x.at || 0) - (y.at || 0));
  // Before-photos taken of a whole project (a type's "photograph everything first" rule).
  projects.filter((p) => inScope(p.id) || !projectIds).forEach((p) => (Array.isArray(p.beforePhotos) ? p.beforePhotos : []).forEach((ph) => {
    if (ph && isImage(ph.uri) && inDay(ph.at, b)) photos.push({ uri: ph.uri, tag: 'before', title: p.name, projectId: p.id, coords: ph.coords || null });
  }));
  // Photos of the whole job and the bid's site photos from the shared index (one per picture; a before-photo already
  // listed above is not listed twice).
  const seenUri = new Set(photos.map((p) => p.uri));
  const pName = new Map(projects.map((p) => [p.id, p.name]));
  for (const m of mediaForDay(media, date)) {
    if (!isImage(m.uri) || seenUri.has(m.uri) || (projectIds && !inScope(m.projectId))) continue;
    seenUri.add(m.uri);
    photos.push({ uri: m.uri, tag: m.tag || (m.source === 'assessment' ? 'assessment' : 'project'), title: pName.get(m.projectId) || (m.source === 'assessment' ? 'Assessment' : 'Project'), projectId: m.projectId || null, at: m.takenAt || m.at || null, caption: m.caption || null, mediaId: m.id, source: m.source });
  }
  photos.sort((x, y) => (x.at || 0) - (y.at || 0));

  // ── Ratios ─────────────────────────────────────────────────────────────────────────────────
  const effectiveClockedMs = Math.max(0, clockedMs - phantomMs);
  const focusRatio = effectiveClockedMs > 0 ? Math.min(1, workedMs / effectiveClockedMs) : 0;
  const untrackedMs = Math.max(0, effectiveClockedMs - workedMs - setupMs - cleanupMs - breakMs);
  // Earned per hour — the one count (jobpay.js earnedByJobType, v0.1.261): fixed / product jobs the labour in the price
  // × share of the estimate finished, T&M hours × the bill rate, ÷ every hour on tasks today — the Field score's rule.
  // `earned` above stays the tasks' own $ values (Earned on tasks); the rate is never task $ ÷ clocked hours any more.
  const byType = earnedByJobType({ tasks: allTasks, projects, departments, jobs, policy: overhead?.policy || null, from: date, to: date, entryIn: (e, t) => byId.has(t.id), taskIn: (t) => byId.has(t.id) });
  const perHour = { value: byType.value, earned: Math.round(byType.earned * 100) / 100, ms: byType.ms };
  const effectiveRate = byType.value || 0;

  // ── By department ──────────────────────────────────────────────────────────────────────────
  const deptRows = new Map();
  const row = (id) => { if (!deptRows.has(id)) deptRows.set(id, { departmentId: id, clockedMs: 0, workedMs: 0, travelMs: 0, done: 0 }); return deptRows.get(id); };
  work.forEach((s) => { if (!phantom.includes(s)) row(s.departmentId || deptId(s.projectId)).clockedMs += s.ms; });
  taskTime.forEach((x) => {
    if (!wd.on) { row(deptId(x.projectId)).workedMs += x.ms; return; }
    for (const e of x.entries) row(wd.forEntry(e, x.task).id).workedMs += e.duration || 0;   // each entry under its own department
    const rest = x.ms - x.entries.reduce((a, e) => a + (e.duration || 0), 0);
    if (rest > 0) row(taskDeptId(x.task)).workedMs += rest;
  });
  completedAll.forEach((t) => { row(taskDeptId(t)).done += 1; });
  drives.forEach((s) => {
    const ids = new Set((s.taskSegments || []).map((g) => deptId(g.projectId)));
    ids.forEach((id) => { row(id).travelMs += Math.round(s.ms * segmentShare(s, (pid) => deptId(pid) === id)); });
  });
  const byDepartment = [...deptRows.values()].filter((r) => r.clockedMs || r.workedMs || r.travelMs || r.done)
    .sort((a, c) => c.clockedMs - a.clockedMs || c.workedMs - a.workedMs);

  // ── Phone (screen time) — only the day's own snapshot; private to the person, never project-scoped ──
  let phoneOut = null;
  if (phone && !projectIds) {
    const distract = distractingApps(focus);
    const m = phoneDayMetrics(phone, distract);
    const sc = phoneScore(phone, phoneGoals(focus), distract);
    const onClock = phoneMsInWindows(phone, distract, work.map((s) => ({ start: s.start, end: s.end })));
    phoneOut = { screenMs: m.screen, distractMs: m.distract, pickups: m.pickups, opens: phone.opens || 0,
      score: sc?.score ?? null, zone: sc?.zone ?? null, parts: sc?.parts || [], distractOnClockMs: onClock,
      apps: (phone.apps || []).slice(0, 10).map((a) => ({ pkg: a.pkg, label: a.label, ms: a.ms, opens: a.opens })) };
  }

  // ── Calendar: what was planned (events, project blocks) against what got clocked ─────────────────
  const planLayer = planItems({ date, events: events.filter((e) => !projectIds || inScope(e.projectId)), blocks: blocks.filter((b) => b.date === date && inScope(b.projectId)), tasks: [], dayPlan: [] });
  const actualLayer = actualItems({ date, sessions: work, tasks: [], now });
  const pva = planVsActual({ plan: planLayer.timed, actual: actualLayer });
  const calendar = { ...pva, events: planLayer.timed.filter((p) => p.type === 'event').map((p) => ({ id: p.id, title: p.title, kind: p.kind, start: p.start, end: p.end, status: p.status, customerName: p.customerName })) };

  const empty = work.length === 0 && travel.length === 0 && completedAll.length === 0 && planned.length === 0 && taskTime.length === 0 && dayNotes.length === 0 && calendar.events.length === 0 && voided.length === 0;

  return {
    date, bounds: b, empty,
    clock: { firstIn, lastOut, clockedMs: effectiveClockedMs, rawClockedMs: clockedMs, byProject: clockByProject, sessions: work, open: clockOpen, ranPastMidnight: clockRanPastMidnight, phantom, phantomMs },
    travel: { ms: travelMs, km: Math.round(travelKm * 10) / 10, trips: drives.length, sessions: travel, neverEnded: travelOpenAllDay },
    time: { workedMs, byProject: workByProject, tasks: taskTime, untrackedMs, focusRatio, longestStretchMs,
      setupMs, cleanupMs, buckets: { setup: bucketTime.setup || [], cleanup: bucketTime.cleanup || [] }, bucketDone,
      overheadPct: effectiveClockedMs > 0 ? Math.round(((setupMs + cleanupMs) / effectiveClockedMs) * 100) : 0 },
    breaks: { ms: breakMs, list: breaks, breakdown: breakBreakdown },
    tasks: { completed, planned, carriedOver, created, skipped, completionRate, estimateAccuracy, doneCount: completedAll.length, plannedCount: planned.length, voided },
    money: { earned, effectiveRate, perHour },
    // CFO phase 1: per job, labour both versions; 5.7: per task / person / type / tag, billable ÷ clocked, absorption.
    // Assistant accounts' (opsMembers.agent) sessions never count.
    profit: pay ? dayMoney({ work: real, tasks: allTasks, projects, jobs, departments, pay, me: uid, inDay: (ms) => inDay(ms, b), overhead, agents: agentUids(members), taskTime, tagIndex: tags ? tagIndexOf(tags) : null }) : null,
    notes: { day: dayNotes, task: taskNotes },
    flow: { switches, skips: flowSkips.length, avgSkipDwellMs, events: dayFlow },
    photos,
    byDepartment,            // [{ departmentId, clockedMs, workedMs, travelMs, done }]
    phone: phoneOut,         // { screenMs, distractMs, pickups, score, zone, parts, distractOnClockMs, apps } | null
    sessions: daySessions,   // clipped, all types, for the timeline + map
    calendar,                // { plannedMin, clockedMin, byProject, eventsDone, eventsMissed, eventsPlanned, events }
    routines: routines.length ? routineDay(routines, routineRuns, date, { now, uid }) : [],   // [{ routineId, name, state, finishMin, goalsMet … }]
  };
}

/** Flat key/value rows for export (CSV, sheet, LLM summary) — one row per metric. */
export function dayReportRows(r) {
  const m = (ms) => Math.round(ms / 60000);
  return [
    ['date', r.date],
    ['clocked_min', m(r.clock.clockedMs)],
    ['first_in', r.clock.firstIn], ['last_out', r.clock.lastOut],
    ['on_task_min', m(r.time.workedMs)],
    ['focus_pct', Math.round(r.time.focusRatio * 100)],
    ['untracked_min', m(r.time.untrackedMs)],
    ['setup_min', m(r.time.setupMs)], ['cleanup_min', m(r.time.cleanupMs)],
    ['break_min', m(r.breaks.ms)],
    ['travel_min', m(r.travel.ms)], ['travel_km', r.travel.km], ['trips', r.travel.trips],
    ['tasks_done', r.tasks.doneCount], ['tasks_planned', r.tasks.plannedCount], ['carried_over', r.tasks.carriedOver.length],
    ['tasks_created', r.tasks.created.length], ['tasks_skipped', r.tasks.skipped.length],
    ['completion_pct', r.tasks.completionRate], ['estimate_accuracy_pct', r.tasks.estimateAccuracy],
    ['earned', r.money.earned], ['effective_rate', Math.round(r.money.effectiveRate)], ['earned_by_job_type', r.money.perHour?.earned ?? 0],
    ['switches', r.flow.switches], ['card_skips', r.flow.skips],
    ['notes', r.notes.day.length + r.notes.task.length], ['photos', r.photos.length],
    ...(r.routines?.length ? [
      ['routines_done', r.routines.filter((x) => x.state === 'done').length], ['routines_scheduled', r.routines.length],
      ['routines_goals_met', r.routines.filter((x) => x.goalsMet).length],
    ] : []),
    ...(r.profit ? [
      ['job_hours', r.profit.total.hours], ['earned_today', r.profit.total.earned],
      ['labour_actual', r.profit.total.labour.actual], ['labour_if_paid_out', r.profit.total.labour.paidOut],
      ['profit_actual', r.profit.total.profit.actual], ['profit_if_paid_out', r.profit.total.profit.paidOut],
      ['profit_per_hour_if_paid_out', r.profit.total.perHour.profitPaidOut],
      ['billable_h', r.profit.utilization.billable], ['clocked_h', r.profit.utilization.clocked], ['billable_pct', r.profit.utilization.pct],
      ...(r.profit.absorption ? [['overhead_charged', r.profit.absorption.charged.actual], ['overhead_day_pool', r.profit.absorption.pool.actual], ['overhead_over_absorbed', r.profit.absorption.over.actual]] : []),
    ] : []),
    ...(r.phone ? [
      ['phone_min', m(r.phone.screenMs)], ['phone_distracting_min', m(r.phone.distractMs)],
      ['phone_pickups', r.phone.pickups], ['phone_score', r.phone.score],
      ['phone_distracting_on_clock_min', r.phone.distractOnClockMs == null ? null : m(r.phone.distractOnClockMs)],
    ] : []),
  ];
}

